import React, { forwardRef, useState } from 'react';
import Avatar from '@amzn/awsui-chat-components-console/avatar';
import ButtonGroup, { ButtonGroupProps } from '@amzn/awsui-components-console/button-group';
import StatusIndicator from '@amzn/awsui-components-console/status-indicator';

import { AuthorAvatarProps } from './config';

const copyToClipboard = (text: string) => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
};

export function ChatBubbleAvatar({ type, name, initials, loading }: AuthorAvatarProps) {
  if (type === 'gen-ai') {
    return <Avatar color="gen-ai" iconName="gen-ai" tooltipText={name} ariaLabel={name} loading={loading}/>;
  }

  return <Avatar initials={initials} tooltipText={name} ariaLabel={name}/>;
}

export function CodeViewActions({ contentToCopy, messageId }: { contentToCopy: string; messageId: string }) {
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  return (
    <ButtonGroup
      ariaLabel="Code snippet actions"
      variant="icon"
      onItemClick={({ detail }) => {
        if (detail.id.startsWith('copy-')) {
          copyToClipboard(contentToCopy);
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
          return;
        }
      }}
      items={[
        {
          type: 'group',
          text: 'Feedback',
          items: [
            {
              type: 'icon-button',
              id: 'run-command',
              iconName: 'play',
              text: 'Run command',
            },
            {
              type: 'icon-button',
              id: 'send-cloudshell',
              iconName: 'script',
              text: 'Send to IDE',
            },
          ],
        },
        {
          type: 'icon-button',
          id: `copy-${messageId}`,
          iconName: copySuccess ? 'status-positive' : 'copy',
          text: copySuccess ? 'Copied!' : 'Copy',
        },
      ]}
    />
  );
}

export const FittedContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <div style={{ position: 'relative', flexGrow: 1 }}>
      <div style={{ position: 'absolute', inset: 0 }}>{children}</div>
    </div>
  );
};

export const ScrollableContainer = forwardRef(function ScrollableContainer(
  { children }: { children: React.ReactNode },
  ref: React.Ref<HTMLDivElement>,
) {
  return (
    <div style={{ position: 'relative', blockSize: '100%' }}>
      <div style={{ position: 'absolute', inset: 0, overflowY: 'auto' }} ref={ref} data-testid="chat-scroll-container">
        {children}
      </div>
    </div>
  );
});

export function FeedbackActions({
                                  contentToCopy,
                                  onFeedbackClick,
                                  userMessage,
                                  aiResponse,
                                  sessionId,
                                  messageId,
                                  hideCopy = false,
                                }: {
  contentToCopy: string;
  onFeedbackClick: (isPositive: boolean) => void;
  userMessage: string;
  aiResponse: string;
  sessionId: string | null;
  messageId: string;
  hideCopy?: boolean;
}) {
  const [feedback, setFeedback] = useState<string>('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const items: ButtonGroupProps.ItemOrGroup[] = [
    {
      type: 'group',
      text: 'Vote',
      items: [
        {
          type: 'icon-button',
          id: 'helpful',
          iconName: feedback === 'helpful' ? 'thumbs-up-filled' : 'thumbs-up',
          text: 'Helpful',
          disabled: !!feedback.length || feedbackSubmitting === 'not-helpful',
          disabledReason: feedbackSubmitting.length
            ? ''
            : feedback === 'helpful'
              ? '"Helpful" feedback has been submitted.'
              : '"Helpful" option is unavailable after "Not helpful" feedback submitted.',
          loading: feedbackSubmitting === 'helpful',
          popoverFeedback:
            feedback === 'helpful' ? (
              <StatusIndicator type="success">Feedback submitted</StatusIndicator>
            ) : (
              'Submitting feedback'
            ),
        },
        {
          type: 'icon-button',
          id: 'not-helpful',
          iconName: feedback === 'not-helpful' ? 'thumbs-down-filled' : 'thumbs-down',
          text: 'Not helpful',
          disabled: !!feedback.length || feedbackSubmitting === 'helpful',
          disabledReason: feedbackSubmitting.length
            ? ''
            : feedback === 'not-helpful'
              ? '"Not helpful" feedback has been submitted.'
              : '"Not helpful" option is unavailable after "Helpful" feedback submitted.',
          loading: feedbackSubmitting === 'not-helpful',
          popoverFeedback:
            feedback === 'helpful' ? (
              <StatusIndicator type="success">Feedback submitted</StatusIndicator>
            ) : (
              'Submitting feedback'
            ),
        },
      ],
    },
    ...(hideCopy ? [] : [{
      type: 'icon-button' as const,
      id: `copy-${messageId}`,
      iconName: (copySuccess ? 'status-positive' : 'copy') as any,
      text: copySuccess ? 'Copied!' : 'Copy',
    }]),
  ];

  return (
    <ButtonGroup
      ariaLabel="Chat actions"
      variant="icon"
      items={items}
      onItemClick={({ detail }): any => {
        if (detail.id.startsWith('copy-')) {
          copyToClipboard(contentToCopy);
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
          return;
        }

        setFeedbackSubmitting(detail.id);

        const isPositive = detail.id === 'helpful';
        setFeedback(detail.id);
        setFeedbackSubmitting('');
        onFeedbackClick(isPositive);
      }}
    />
  );
}